import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useTheme } from '@mui/material/styles';

export default function TimeHeatmap({ data = [] }) {
  const theme = useTheme();
  
  const chartData = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Initialize count matrix
    const matrix = days.map(() => Array(24).fill(0));
    
    // Process data
    if (Array.isArray(data)) {
      data.forEach(record => {
        if (record && record.timestamp) {
          try {
            const date = new Date(record.timestamp);
            if (!isNaN(date.getTime())) {
              const hour = date.getHours();
              const day = date.getDay();
              matrix[day][hour]++;
            }
          } catch (error) {
            console.error('Invalid date format:', record.timestamp);
          }
        }
      });
    }
    
    // Format for ApexCharts
    return days.map((day, dayIndex) => ({
      name: day,
      data: matrix[dayIndex].map((count, hour) => ({
        x: `${hour}:00`,
        y: count
      }))
    }));
  }, [data]);

  const options = {
    chart: {
      type: 'heatmap',
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      background: 'transparent',
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const hour = parseInt(config.w.config.series[config.seriesIndex].data[config.dataPointIndex].x);
          const day = config.w.config.series[config.seriesIndex].name;
          console.log(`Selected: ${day} at ${hour}:00`);
        }
      }
    },
    dataLabels: {
      enabled: true,
      style: {
        colors: [theme.palette.getContrastText(theme.palette.primary.main)]
      },
      formatter: (val) => val === 0 ? '' : val
    },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const hour = w.config.series[seriesIndex].data[dataPointIndex].x;
        const day = w.config.series[seriesIndex].name;
        const value = w.config.series[seriesIndex].data[dataPointIndex].y;
        return `
          <div class="custom-tooltip" style="padding: 8px;">
            <div><b>${day} at ${hour}</b></div>
            <div>Sessions: ${value}</div>
          </div>
        `;
      }
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.15,
        }
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'darken',
          value: 0.35
        }
      }
    },
    colors: [theme.palette.primary.main],
    title: {
      text: 'Charging Activity by Time of Day',
      align: 'left',
      style: {
        color: theme.palette.text.primary
      }
    },
    xaxis: {
      type: 'category',
      labels: {
        style: {
          colors: theme.palette.text.secondary
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: theme.palette.text.secondary
        }
      }
    },
    theme: {
      mode: theme.palette.mode
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        colorScale: {
          ranges: [
            {
              from: 0,
              to: 0,
              name: 'no activity',
              color: theme.palette.grey[200]
            }
          ]
        }
      }
    }
  };

  return (
    <ReactApexChart
      options={options}
      series={chartData}
      type="heatmap"
      height={350}
    />
  );
}